import * as React from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../core/store';
import { useEffect } from 'react';
import {
  FormField,
  FormFieldControl,
  getFormControl
} from '../../core/util/form-helpers';
import { Button, Form, Spin } from 'antd';
import { requestTokenForFirebaseUser } from './LoginActions';
import { HKActionType } from '../../core/types';
import { AUTH_TOKEN_KEY, EMAIL_REGEX } from '../../core/util/constants';
import Logo from 'assets/illustrations/hk-logo-light.svg';
import './Login.scss';
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../../firebase';
import Icon from '@ant-design/icons';
import { ReactComponent as GoogleIcon } from '../../assets/icons/google.svg';

const Login: React.FC = () => {
  const { loading, error, accessToken, loggedIn } = useSelector(
    (state: RootState) => state.login
  );
  const [form] = Form.useForm();
  const { getFieldsError } = form;
  const dispatch = useDispatch();
  const history = useHistory();
  const GoogleAuth = new GoogleAuthProvider();

  const onFinish = (values: any) => {
    const validationErrors = Object.values(getFieldsError());
    if (validationErrors.some((e) => e.errors.length > 0)) {
      return;
    }
    signInEmail(values.email, values.password);
  };

  const signInEmail = (email: string, password: string) => {
    signInWithEmailAndPassword(auth, email, password)
      .then((_userCredential) => {
        fetchApiAccessToken();
      })
      .catch((error) => {
        console.log(error.code);
        console.log(error.message);
      });
  };

  const signInWithGoogle = (): void => {
    signInWithPopup(auth, GoogleAuth)
      .then((result: any) => {
        GoogleAuthProvider.credentialFromResult(result);
        fetchApiAccessToken();
      })
      .catch((error: any) => {
        console.log(error.code);
        console.log(error.message);
      });
  };

  /**
   * Fetches the HK API access token for the current Firebase user.
   *
   * It is used to authenticate the user with the API.
   * Upon successful authentication, the user is redirected to the homes page.
   *
   * @returns {Promise<void>} - A promise that resolves when the API access token is fetched.
   */
  const fetchApiAccessToken = async (): Promise<void> => {
    const firebaseToken = await auth.currentUser?.getIdToken();
    if (!firebaseToken) throw new Error('No Firebase ID token found');
    dispatch(requestTokenForFirebaseUser(firebaseToken));
  };

  useEffect(() => {
    if (loggedIn && !!accessToken) {
      localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
      history.push('/homes');
    }
  }, [loggedIn, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const loginFields: FormField[] = [
    {
      name: 'email',
      label: 'Email',
      placeholder: 'Enter email',
      control: FormFieldControl.Input,
      rules: [
        { required: true, message: 'Invalid email', pattern: EMAIL_REGEX }
      ]
    },
    {
      name: 'password',
      label: 'Password',
      placeholder: 'Enter email',
      control: FormFieldControl.InputPassword,
      rules: [{ required: true, message: 'Missing password' }]
    }
  ];

  return (
    <div className="hk-login">
      <img className="hk-logo" src={Logo} alt="Logo" />
      <Form
        form={form}
        layout="vertical"
        className="hk-form confirm dark"
        onFinish={onFinish}
        autoComplete="off"
      >
        <h1>
          Welcome <b>Back</b>
        </h1>
        <h6>Enter your details below.</h6>
        {loading && <Spin className="hk-spinner" size="large" />}
        {!loading && (
          <>
            {loginFields.map((field) => {
              return getFormControl(field, HKActionType.Add, {});
            })}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: '300px', margin: '24px 0' }}
              >
                Sign In
              </Button>
            </Form.Item>
            <div id="sign-in-divider">
              <span>or</span>
              <hr />
            </div>
            <Form.Item>
              <Button className="hk-google-button" onClick={signInWithGoogle}>
                <Icon component={GoogleIcon} />
                Sign In with Google
              </Button>
            </Form.Item>
          </>
        )}
        {!!error && <div className="hk-login-error">{error}</div>}
      </Form>
    </div>
  );
};

export default Login;
